import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Title2,
  Body1,
  Body1Strong,
  Spinner,
  Button,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Badge,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import NewRequestDrawer, { LeaveTypeOption } from "../components/NewRequestDrawer";
import { fetchWithRetry } from "../lib/fetchWithRetry";

interface CalendarLeave {
  id: string;
  requestNumber: number;
  status: "approved" | "pending";
  leaveTypeId: string;
  leaveTypeName: string;
  requesterId: string;
  requesterName: string;
  departmentName: string | null;
  startDate: string;
  endDate: string;
  halfDay: boolean;
  amPm: "AM" | "PM" | null;
}

interface CalendarHoliday {
  id: string;
  name: string;
  date: string;
}

interface CalendarEvent {
  id: string;
  name: string;
  date: string;
  isHalfDay: boolean;
  amPm: "AM" | "PM" | null;
}

interface CalendarResponse {
  from: string;
  to: string;
  leaves: CalendarLeave[];
  holidays: CalendarHoliday[];
  events: CalendarEvent[];
}

const PILLS_PER_CELL = 3;
const ALL_DEPARTMENTS = "__all__";

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    flexWrap: "wrap",
    marginBottom: tokens.spacingVerticalS
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap"
  },
  monthTitle: {
    minWidth: "180px",
    textAlign: "center"
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden"
  },
  dayHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalXS,
    textAlign: "center",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2
  },
  cell: {
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: "128px",
    padding: tokens.spacingVerticalXS,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover
    }
  },
  cellOutsideMonth: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3
  },
  cellToday: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: "-2px"
  },
  cellWeekend: {
    backgroundColor: tokens.colorNeutralBackground2
  },
  dayNumberRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "2px"
  },
  dayNumber: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold
  },
  holidayLabel: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorPaletteBerryForeground2,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightSemibold
  },
  eventLabel: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    fontStyle: "italic"
  },
  pill: {
    fontSize: tokens.fontSizeBase200,
    padding: "2px 6px",
    borderRadius: tokens.borderRadiusSmall,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    lineHeight: "16px"
  },
  pillApproved: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2
  },
  pillPending: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2
  },
  moreLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold
  },
  legendPill: {
    fontSize: tokens.fontSizeBase200,
    padding: "2px 6px",
    borderRadius: tokens.borderRadiusSmall
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  detailRow: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`
  }
});

// All date arithmetic uses UTC midnight to avoid TZ drift.
function dateFromIso(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function isoFromDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = dateFromIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoFromDate(d);
}

function firstOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function todayUtcLocal(): Date {
  // Today, anchored to the user's local calendar day, projected to UTC midnight
  // so the rest of the arithmetic stays consistent.
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

function monthGridStart(monthStart: Date): Date {
  // Walk back to the Sunday on or before the 1st.
  const d = new Date(monthStart);
  const dow = d.getUTCDay(); // 0 = Sun
  d.setUTCDate(d.getUTCDate() - dow);
  return d;
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDayNumber(iso: string): string {
  return String(dateFromIso(iso).getUTCDate());
}

function formatLeaveDates(l: CalendarLeave): string {
  const fmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  if (l.startDate === l.endDate) return fmt.format(dateFromIso(l.startDate));
  return `${fmt.format(dateFromIso(l.startDate))} – ${fmt.format(dateFromIso(l.endDate))}`;
}

function formatDayHeading(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC"
  }).format(dateFromIso(iso));
}

function firstName(n: string): string {
  const trimmed = n.trim();
  const idx = trimmed.indexOf(" ");
  return idx === -1 ? trimmed : trimmed.slice(0, idx);
}

interface CalendarProps {
  // Embed mode: hits the public /api/embed-calendar endpoint, skips the
  // leave-types fetch + NewRequestDrawer wiring, and renders the day-detail
  // dialog without the "New request on this day" action. Used by the
  // SharePoint iframe at /embed/calendar.
  embedMode?: boolean;
}

export default function Calendar({ embedMode = false }: CalendarProps) {
  const styles = useStyles();
  const toasterId = useId("calendar-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const [viewMonth, setViewMonth] = useState<Date>(() => firstOfMonth(todayUtcLocal()));
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [department, setDepartment] = useState<string>(ALL_DEPARTMENTS);
  const [detailDate, setDetailDate] = useState<string | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);
  const [drawerDate, setDrawerDate] = useState<string | null>(null);

  const gridStart = useMemo(() => monthGridStart(viewMonth), [viewMonth]);
  const gridIsoDays = useMemo(() => {
    const days: string[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setUTCDate(d.getUTCDate() + i);
      days.push(isoFromDate(d));
    }
    return days;
  }, [gridStart]);

  const fromIso = gridIsoDays[0];
  const toIso = gridIsoDays[gridIsoDays.length - 1];

  const calendarApiPath = embedMode ? "/api/embed-calendar" : "/api/calendar";

  const load = useCallback(() => {
    setError(null);
    return fetchWithRetry(
      `${calendarApiPath}?from=${fromIso}&to=${toIso}`,
      undefined,
      { onWarming: () => setWarming(true) }
    )
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return (await r.json()) as CalendarResponse;
      })
      .then((d) => { setData(d); setWarming(false); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [fromIso, toIso, calendarApiPath]);

  useEffect(() => {
    setData(null);
    setWarming(false);
    load();
  }, [load]);

  useEffect(() => {
    // Embed mode is read-only — no creating from the dialog, so skip the
    // leave-types fetch (which is /api/leave-types, an authenticated endpoint
    // that would 401 in the embed context anyway).
    if (embedMode) return;
    fetchWithRetry("/api/leave-types", undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setLeaveTypes(d.leaveTypes as LeaveTypeOption[]))
      .catch(() => {
        // Non-fatal — without leave types, the "New request" action on a day cell stays disabled.
      });
  }, [embedMode]);

  const departments = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const l of data.leaves) {
      if (l.departmentName) set.add(l.departmentName);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filteredLeaves = useMemo(() => {
    if (!data) return [] as CalendarLeave[];
    if (department === ALL_DEPARTMENTS) return data.leaves;
    return data.leaves.filter((l) => l.departmentName === department);
  }, [data, department]);

  const leavesByDay = useMemo(() => {
    const map = new Map<string, CalendarLeave[]>();
    for (const iso of gridIsoDays) map.set(iso, []);
    for (const l of filteredLeaves) {
      for (const iso of gridIsoDays) {
        if (iso >= l.startDate && iso <= l.endDate) {
          map.get(iso)!.push(l);
        }
      }
    }
    return map;
  }, [filteredLeaves, gridIsoDays]);

  const holidaysByDay = useMemo(() => {
    const map = new Map<string, CalendarHoliday[]>();
    if (!data) return map;
    for (const h of data.holidays) {
      const arr = map.get(h.date) ?? [];
      arr.push(h);
      map.set(h.date, arr);
    }
    return map;
  }, [data]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    if (!data) return map;
    for (const e of data.events) {
      const arr = map.get(e.date) ?? [];
      arr.push(e);
      map.set(e.date, arr);
    }
    return map;
  }, [data]);

  const todayIso = useMemo(() => isoFromDate(todayUtcLocal()), []);
  const viewMonthIndex = viewMonth.getUTCMonth();
  const viewMonthLabel = `${MONTH_NAMES[viewMonthIndex]} ${viewMonth.getUTCFullYear()}`;

  function goPrev() {
    const d = new Date(viewMonth);
    d.setUTCMonth(d.getUTCMonth() - 1);
    setViewMonth(d);
  }
  function goNext() {
    const d = new Date(viewMonth);
    d.setUTCMonth(d.getUTCMonth() + 1);
    setViewMonth(d);
  }
  function goToday() {
    setViewMonth(firstOfMonth(todayUtcLocal()));
  }

  // In embed mode the dialog never offers the create action.
  const canCreate = !embedMode && leaveTypes.length > 0;

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <Title2 as="h2" block>Calendar</Title2>
        <div className={styles.toolbar}>
          <Button size="small" appearance="subtle" onClick={goPrev}>‹ Prev</Button>
          <Body1Strong className={styles.monthTitle}>{viewMonthLabel}</Body1Strong>
          <Button size="small" appearance="subtle" onClick={goNext}>Next ›</Button>
          <Button size="small" appearance="secondary" onClick={goToday}>Today</Button>
          <Dropdown
            placeholder="All departments"
            selectedOptions={[department]}
            value={department === ALL_DEPARTMENTS ? "All departments" : department}
            onOptionSelect={(_, d) => setDepartment(String(d.optionValue ?? ALL_DEPARTMENTS))}
          >
            <Option value={ALL_DEPARTMENTS} text="All departments">All departments</Option>
            {departments.map((d) => (
              <Option key={d} value={d} text={d}>{d}</Option>
            ))}
          </Dropdown>
        </div>
      </div>

      <div className={styles.legend}>
        <span className={`${styles.legendPill} ${styles.pillApproved}`}>Approved</span>
        <span className={`${styles.legendPill} ${styles.pillPending}`}>Pending</span>
        <span>Holidays in red · special events in italic</span>
      </div>

      {error && <Body1 as="p" className={styles.error}>Error loading calendar: {error}</Body1>}

      {!data && !error && (
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      )}

      {data && (
        <div className={styles.grid}>
          {DOW_LABELS.map((d) => (
            <div key={d} className={styles.dayHeader}>{d}</div>
          ))}
          {gridIsoDays.map((iso) => {
            const d = dateFromIso(iso);
            const outsideMonth = d.getUTCMonth() !== viewMonthIndex;
            const isToday = iso === todayIso;
            const dow = d.getUTCDay();
            const isWeekend = dow === 0 || dow === 6;
            const dayLeaves = leavesByDay.get(iso) ?? [];
            const dayHolidays = holidaysByDay.get(iso) ?? [];
            const dayEvents = eventsByDay.get(iso) ?? [];
            const visible = dayLeaves.slice(0, PILLS_PER_CELL);
            const hidden = dayLeaves.length - visible.length;

            const classNames = [styles.cell];
            if (outsideMonth) classNames.push(styles.cellOutsideMonth);
            else if (isWeekend) classNames.push(styles.cellWeekend);
            if (isToday) classNames.push(styles.cellToday);

            return (
              <div
                key={iso}
                className={classNames.join(" ")}
                role="button"
                tabIndex={0}
                onClick={() => setDetailDate(iso)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setDetailDate(iso); }}
              >
                <div className={styles.dayNumberRow}>
                  <span className={styles.dayNumber}>{formatDayNumber(iso)}</span>
                </div>
                {dayHolidays.map((h) => (
                  <div key={h.id} className={styles.holidayLabel} title={h.name}>★ {h.name}</div>
                ))}
                {dayEvents.map((ev) => (
                  <div key={ev.id} className={styles.eventLabel} title={ev.name}>
                    {ev.isHalfDay ? `½ ${ev.name}` : ev.name}
                  </div>
                ))}
                {visible.map((l) => (
                  <div
                    key={l.id}
                    className={`${styles.pill} ${l.status === "approved" ? styles.pillApproved : styles.pillPending}`}
                    title={`${l.requesterName} · ${l.leaveTypeName} · ${formatLeaveDates(l)} (${l.status})`}
                  >
                    {l.halfDay ? "½ " : ""}{firstName(l.requesterName)}
                  </div>
                ))}
                {hidden > 0 && (
                  <div className={styles.moreLabel}>+ {hidden} more</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && filteredLeaves.length === 0 && (
        <div className={styles.empty} style={{ marginTop: tokens.spacingVerticalM }}>
          <Body1>No leave requests in this window{department !== ALL_DEPARTMENTS ? ` for ${department}` : ""}.</Body1>
        </div>
      )}

      <DayDetailDialog
        date={detailDate}
        leaves={detailDate ? leavesByDay.get(detailDate) ?? [] : []}
        holidays={detailDate ? holidaysByDay.get(detailDate) ?? [] : []}
        events={detailDate ? eventsByDay.get(detailDate) ?? [] : []}
        canCreate={canCreate}
        onClose={() => setDetailDate(null)}
        onCreate={(iso) => {
          setDetailDate(null);
          setDrawerDate(iso);
        }}
      />

      {!embedMode && (
        <NewRequestDrawer
          open={drawerDate !== null}
          leaveTypes={leaveTypes}
          defaultStartDate={drawerDate ?? undefined}
          onClose={() => setDrawerDate(null)}
          onCreated={() => {
            dispatchToast(
              <Toast><ToastTitle>Request submitted</ToastTitle></Toast>,
              { intent: "success" }
            );
            load();
          }}
        />
      )}
    </div>
  );
}

interface DayDetailDialogProps {
  date: string | null;
  leaves: CalendarLeave[];
  holidays: CalendarHoliday[];
  events: CalendarEvent[];
  canCreate: boolean;
  onClose: () => void;
  onCreate: (iso: string) => void;
}

function DayDetailDialog({ date, leaves, holidays, events, canCreate, onClose, onCreate }: DayDetailDialogProps) {
  const styles = useStyles();
  if (!date) return null;
  return (
    <Dialog open={date !== null} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{formatDayHeading(date)}</DialogTitle>
          <DialogContent>
            {holidays.length > 0 && (
              <div className={styles.detailRow} style={{ marginBottom: tokens.spacingVerticalM }}>
                <Body1Strong>Holiday</Body1Strong>
                {holidays.map((h) => <Body1 key={h.id}>★ {h.name}</Body1>)}
              </div>
            )}
            {events.length > 0 && (
              <div className={styles.detailRow} style={{ marginBottom: tokens.spacingVerticalM }}>
                <Body1Strong>Special event</Body1Strong>
                {events.map((e) => (
                  <Body1 key={e.id}>
                    {e.isHalfDay ? `½ ${e.name}${e.amPm ? ` (${e.amPm})` : ""}` : e.name}
                  </Body1>
                ))}
              </div>
            )}
            {leaves.length === 0 ? (
              <Body1>No one is out on this day.</Body1>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
                {leaves.map((l) => (
                  <div key={l.id} className={styles.detailRow}>
                    <Body1Strong>
                      {l.requesterName}
                      {l.departmentName ? ` · ${l.departmentName}` : ""}
                    </Body1Strong>
                    <Body1>
                      {l.leaveTypeName} · {formatLeaveDates(l)}
                      {l.halfDay ? ` · ½ day${l.amPm ? ` (${l.amPm})` : ""}` : ""}
                      {" "}
                      <Badge
                        size="small"
                        appearance={l.status === "approved" ? "filled" : "outline"}
                        color={l.status === "approved" ? "success" : "warning"}
                      >
                        {l.status}
                      </Badge>
                    </Body1>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {canCreate && (
              <Button appearance="secondary" onClick={() => onCreate(date)}>
                New request on this day
              </Button>
            )}
            <Button appearance="primary" onClick={onClose}>Close</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
